import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService.js';
import PasswordStrengthIndicator from '../common/PasswordStrengthIndicator.jsx';

const UpdatePassword = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    // Validate new password strength
    const passwordErrors = validatePassword(formData.newPassword);
    if (passwordErrors.length > 0) {
      setError('New password requirements:\n' + passwordErrors.map(err => `• ${err}`).join('\n'));
      setLoading(false);
      return;
    }

    try {
      await authService.updatePassword(formData.oldPassword, formData.newPassword);
      setSuccess('Password updated successfully!');
      
      // Clear form
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Redirect to dashboard after success
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Update password error:', error);
      
      let errorMessage = 'Failed to update password. Please try again.';
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        if (error.response.data?.error?.includes('oldPassword') || error.response.data?.error?.includes('current')) {
          errorMessage = 'Current password is incorrect. Please enter your current password.';
        } else if (error.response.data?.error?.includes('newPassword') || error.response.data?.error?.includes('password')) {
          errorMessage = 'New password does not meet security requirements. Please ensure it is at least 8 characters long.';
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 401) {
        errorMessage = 'Current password is incorrect. Please check and try again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.error) {
        errorMessage = error.error;
      } else if (error.message && error.message !== 'Network Error') {
        errorMessage = error.message;
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network connection failed. Please check your internet connection.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="form-container auth-security-container">
        <div className="auth-header">
          <h2>Change Password</h2>
          <p>Update your password with secure encryption</p>
        </div>
        
        {error && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-triangle"></i>
            <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <i className="fas fa-check-circle"></i>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group secure">
            <label htmlFor="oldPassword">Current Password:</label>
            <div className="password-input-container">
              <input
                type={showOldPassword ? "text" : "password"}
                id="oldPassword"
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Enter your current password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={toggleOldPasswordVisibility}
                disabled={loading}
                aria-label={showOldPassword ? "Hide password" : "Show password"}
              >
                <i className={`fas ${showOldPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div className="form-group secure">
            <label htmlFor="newPassword">New Password:</label>
            <div className="password-input-container">
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                required
                disabled={loading}
                minLength="8"
                placeholder="Create a strong new password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={toggleNewPasswordVisibility}
                disabled={loading}
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            <PasswordStrengthIndicator password={formData.newPassword} />
          </div>

          <div className="form-group secure">
            <label htmlFor="confirmPassword">Confirm New Password:</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={loading}
                minLength="8"
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={toggleConfirmPasswordVisibility}
                disabled={loading}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

         
          <button 
            type="submit" 
            className={`btn btn-primary auth-submit-btn ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-shield-alt"></i>
                Updating Password Securely...
              </>
            ) : (
              <>
                <i className="fas fa-key"></i>
                Update Password
              </>
            )}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button 
            type="button" 
            onClick={() => navigate('/dashboard')}
            className="btn btn-secondary"
            disabled={loading}
          >
            <i className="fas fa-arrow-left"></i> Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;